// API configuration for different environments
const getApiBaseUrl = () => {
  // First, try React environment variables (if set during build)
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Second, try regular environment variables
  if (process.env.BACKEND_URL) {
    return process.env.BACKEND_URL;
  }
  
  // Third, check if we're running on Firebase Hosting and use Cloud Run backend
  if (typeof window !== 'undefined' && window.location.hostname) {
    const hostname = window.location.hostname;
    
    if (hostname.includes('firebaseapp.com') || 
        hostname.includes('web.app') ||
        hostname.includes('viraltrade.in') ||
        hostname.includes('viraltrade-19032')) {
      return 'https://viral-trade-backend-1081469852720.asia-south1.run.app';
    }
    
    // Fourth, check if we're running on Cloud Run and use hardcoded URL
    if (hostname.includes('run.app')) {
      return 'https://viral-trade-backend-1081469852720.asia-south1.run.app';
    }
  }
  
  // Finally, fallback to localhost for development
  return 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();

class ApiClient {
  constructor(baseUrl = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Generic HTTP methods
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  // Health checks
  async getHealth() {
    return this.request('/health');
  }

  async getLLMHealth(clientType) {
    return this.request(`/llm/${clientType}/health`);
  }

  // LLM Chat
  async chat(messages, clientType = 'openai', options = {}) {
    const requestBody = {
      messages,
      client_type: clientType,
      symbol: options.symbol,
      enable_rag: options.enableRag ?? true,
      include_financial_data: options.includeFinancialData ?? true,
      analysis_type: options.analysisType ?? 'general',
      model_name: options.modelName,
      max_tokens: options.maxTokens,
      temperature: options.temperature
    };
    
    console.log('🔍 API Client - Request body being sent:', requestBody);
    
    return this.request('/api/chat', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  }

  // Financial data
  async getStockInfo(symbol) {
    // Legacy path kept for backward compatibility; prefer getStockInfoBasic
    return this.request(`/financial/stock/${symbol}`);
  }

  async getFinancialStatements(symbol) {
    return this.request(`/financial/statements/${symbol}`);
  }

  async getStockAnalysis(symbol, analysisType = 'comprehensive', includeNews = true, includeSentiment = true) {
    return this.request('/analyze/stock', {
      method: 'POST',
      body: JSON.stringify({
        symbol,
        analysis_type: analysisType,
        include_news: includeNews,
        include_sentiment: includeSentiment
      }),
    });
  }

  // Basic company/stock info (name, sector, price, pe, dividend, etc.)
  async getStockInfoBasic(symbol) {
    return this.request(`/stock-info/${encodeURIComponent(symbol)}`);
  }

  // RAG and Document management
  async uploadDocument(file) {
    const formData = new FormData();
    formData.append('file', file);
    
    const url = `${this.baseUrl}/upload`;
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  }

  async queryRAG(query) {
    return this.request('/rag/query', {
      method: 'POST',
      body: JSON.stringify({ query }),
    });
  }

  async getDocuments() {
    return this.request('/documents');
  }

  async deleteDocument(docId) {
    return this.request(`/documents/${docId}`, {
      method: 'DELETE',
    });
  }

  // Favorites management
  async getFavorites(userId) {
    const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
    return this.request(`/api/favorites${qs}`);
  }

  async addFavorite(symbol, name = null, userId) {
    // const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
    return this.request(`/api/favorites`, {
      method: 'POST',
      body: JSON.stringify({ symbol, name, user_id: userId }),
    });
  }

  async removeFavorite(symbol, userId) {
    const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
    return this.request(`/api/favorites/${encodeURIComponent(symbol)}${qs}`, {
      method: 'DELETE',
    });
  }

  async checkFavorite(symbol, userId) {
    const qs = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
    return this.request(`/api/favorites/check/${encodeURIComponent(symbol)}${qs}`);
  }

  // Legacy endpoints (for backward compatibility)
  async getFinancialData() {
    return this.request('/financial-data');
  }

  async chatRAG(message, query, symbol) {
    return this.request('/chat-rag', {
      method: 'POST',
      body: JSON.stringify({ message, query, symbol }),
    });
  }
}

// Create and export a default instance
const apiClient = new ApiClient();

export default apiClient;
export { ApiClient };